import React, { useMemo } from "react";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useParams } from "react-router-dom";
import { useGetPlanDetailByTab, useGetParameter } from "../../../hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { BadgeList } from "@shared/components/BadgeList";

const parseStringList = (val: any): string[] => {
  if (!val) return [];
  const list: string[] = [];
  const rawArray = Array.isArray(val) ? val.flat() : [val];
  rawArray.forEach((item: any) => {
    if (typeof item === "string" && item.trim()) {
      item.split(",").forEach((s) => {
        const trimmed = s.trim();
        if (trimmed) list.push(trimmed);
      });
    } else if (item !== null && item !== undefined && item !== "") {
      list.push(String(item));
    }
  });
  return list;
};

export const Deposits = () => {
  const { id } = useParams();
  const currency = useCurrency();
  const { data: planDetail } = useGetPlanDetailByTab(id, "deposits");
  const { data: globalParamData } = useGetParameter();

  const parameterOptions = useMemo(() => {
    const raw = Array.isArray(globalParamData)
      ? globalParamData
      : globalParamData?.result || globalParamData?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [globalParamData]);

  const resolveParamName = (rule: any, group?: any) => {
    const groupName = group?.parameterName || group?.parameter_name;
    if (groupName && groupName.toLowerCase() !== "common" && groupName !== "NA")
      return groupName;
    const rawName = rule.parameterName || rule.parameter_name;
    if (rawName && rawName.toLowerCase() !== "common" && rawName !== "NA")
      return rawName;
    const pId = rule.parameterId || rule.parameter_id || rule.parameter;
    if (!pId) return "—";
    const found = parameterOptions.find(
      (p: any) =>
        String(p.id) === String(pId) || String(p.parameter_id) === String(pId),
    );
    return found
      ? found.name || found.parameter_name || String(pId)
      : String(pId);
  };

  const res = planDetail?.result || {};
  const depositsConfig = res.deposits_config || res.depositsConfig || res;
  const enabled =
    depositsConfig.enabled !== undefined
      ? depositsConfig.enabled
      : res.enabled !== undefined
        ? res.enabled
        : false;

  const rulesGrouped =
    res.rulesGrouped ||
    res.rules_grouped ||
    depositsConfig.rulesGrouped ||
    depositsConfig.rules_grouped ||
    [];
  const flatRules = res.rules || depositsConfig.rules || [];

  const mapRuleToRow = (
    rule: any,
    groupIndex: number,
    idx: number,
    group?: any,
  ) => {
    const payInterest = !!(rule.payInterest ?? rule.pay_interest);
    const interestTiers = rule.interestTiers || rule.interest_tiers || [];
    const eligibleAfter = rule.eligibleAfter ?? rule.eligible_after ?? 0;
    const eligibleAfterUnit =
      rule.eligibleAfterUnit || rule.eligible_after_unit || "months";

    let interestText = "No";
    let interestSubText = "";

    if (payInterest) {
      if (interestTiers.length > 0) {
        const tiersFormatted = interestTiers
          .map((t: any) => {
            const rate = t.rate ?? 0;
            const from = t.fromMonth ?? t.from_month ?? t.from ?? 0;
            const to = t.toMonth ?? t.to_month ?? t.to;
            return to !== null && to !== undefined && to !== ""
              ? `${rate}% (Mo ${from}-${to})`
              : `${rate}% (from Mo ${from})`;
          })
          .join(", ");
        interestText = tiersFormatted;
        interestSubText = `applied after ${eligibleAfter} ${eligibleAfterUnit}`;
      } else {
        interestText = "Yes";
        if (eligibleAfter) {
          interestSubText = `applied after ${eligibleAfter} ${eligibleAfterUnit}`;
        }
      }
    }

    const basis =
      (rule.basis || "").toLowerCase() === "flat" ? "Flat" : "% avg bill";
    const amountVal =
      rule.amount ?? rule.depositAmount ?? rule.deposit_amount ?? 0;
    const deposit =
      (rule.basis || "").toLowerCase() === "flat"
        ? typeof amountVal === "number"
          ? `${currency} ${amountVal}`
          : String(amountVal).includes(currency)
            ? amountVal
            : `${currency} ${amountVal}`
        : `${rule.multiplier || 1}x monthly bill`;

    return {
      id: `${groupIndex}-${idx}`,
      depositName: rule.depositName || rule.deposit_name || "Security Deposit",
      utilityService: rule.utilityServices || rule.utility_services || [],
      parameterName: resolveParamName(rule, group),
      value: rule.parameterValues || rule.parameter_values || rule.value || [],
      basis,
      deposit,
      payInterest,
      interest: interestText,
      interestSub: interestSubText,
    };
  };

  const commonColumns: TableColumn<any>[] = [
    {
      key: "depositName",
      header: "Deposit Name",
      width: "w-[40%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {row.depositName || "Security Deposit"}
        </span>
      ),
    },
    {
      key: "deposit",
      header: "Deposit Amount",
      width: "w-[30%]",
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 rounded-full px-3 font-medium"
        >
          {row.deposit}
        </Badge>
      ),
    },
    {
      key: "interest",
      header: "Interest",
      width: "w-[30%]",
      render: (val: any, row: any) => (
        <div>
          {row.payInterest ? (
            <div className="text-xs text-gray-900 font-medium">
              {row.interest}
              {row.interestSub && (
                <div className="text-gray-500 font-normal text-[11px]">
                  {row.interestSub}
                </div>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-600 font-medium">No</span>
          )}
        </div>
      ),
    },
  ];

  const paramWiseColumns: TableColumn<any>[] = [
    {
      key: "depositName",
      header: "Deposit Name",
      width: "w-[18%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-900">
          {row.depositName || "Deposit Rule"}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-[16%]",
      render: (val: any, row: any) => {
        const items = parseStringList(row.utilityService);
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-cyan-50 text-cyan-700 border-cyan-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "parameter",
      header: "Parameter",
      width: "w-[16%]",
      render: (val: any, row: any) => (
        <span className="font-medium text-gray-800">{row.parameterName}</span>
      ),
    },
    {
      key: "value",
      header: "Applies To",
      width: "w-[16%]",
      render: (val: any, row: any) => {
        const items = parseStringList(row.value);
        return (
          <BadgeList
            items={items}
            count={2}
            badgeClassName="bg-purple-50 text-purple-700 border-purple-200"
            fallback={<span className="text-gray-400 text-xs">All</span>}
          />
        );
      },
    },
    {
      key: "basis",
      header: "Basis",
      width: "w-[12%]",
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className={`${
            row.basis === "Flat"
              ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-purple-50 text-purple-700 border-purple-200"
          } rounded-full px-3`}
        >
          {row.basis}
        </Badge>
      ),
    },
    {
      key: "deposit",
      header: "Deposit Amount",
      width: "w-[12%]",
      render: (val: any, row: any) => (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-600 border-green-200 rounded-full px-3 font-medium"
        >
          {row.deposit}
        </Badge>
      ),
    },
    {
      key: "interest",
      header: "Interest",
      width: "w-[10%]",
      render: (val: any, row: any) => (
        <div>
          {row.payInterest ? (
            <div className="text-xs text-gray-900 font-medium">
              {row.interest}
              {row.interestSub && (
                <div className="text-gray-500 font-normal text-[11px]">
                  {row.interestSub}
                </div>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-600 font-medium">No</span>
          )}
        </div>
      ),
    },
  ];

  const totalRulesCount = React.useMemo(() => {
    if (!enabled) return 0;
    if (rulesGrouped.length > 0) {
      return rulesGrouped.reduce(
        (acc: number, group: any) => acc + (group.rules?.length || 0),
        0,
      );
    }
    return flatRules.length;
  }, [enabled, rulesGrouped, flatRules]);

  const overallMode = React.useMemo(() => {
    if (rulesGrouped.length > 0) {
      const hasParam = rulesGrouped.some(
        (g: any) =>
          g.parameter_name?.toLowerCase() !== "common" &&
          g.parameterName?.toLowerCase() !== "common" &&
          (g.parameter_id || g.parameterId),
      );
      if (hasParam) return "parameter-wise";
    }
    const firstRule = flatRules[0] || rulesGrouped[0]?.rules?.[0];
    if (firstRule) {
      const m = (firstRule.mode || "").toLowerCase();
      if (m === "parameter-wise" || m === "parameter_wise")
        return "parameter-wise";
    }
    return "common";
  }, [rulesGrouped, flatRules]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900">
              Deposit Rules
            </h3>
            <Badge
              variant="outline"
              className="bg-purple-50 text-purple-700 border-purple-200 rounded-full px-2 py-0.5 text-xs capitalize"
            >
              {overallMode}
            </Badge>
            {totalRulesCount > 0 && (
              <Badge
                variant="outline"
                className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-full px-2 py-0.5 text-xs"
              >
                {totalRulesCount} {totalRulesCount === 1 ? "rule" : "rules"}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500">
            The deposit amount and interest schedule are independently set per
            rule. At signup the engine matches the consumer's parameters and
            applies the highest priority rule.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {!enabled || totalRulesCount === 0 ? (
          <div className="text-sm text-gray-500 px-2">
            No deposits configured
          </div>
        ) : rulesGrouped.length > 0 ? (
          rulesGrouped.map((group: any, groupIndex: number) => {
            const rules = group.rules || [];
            const firstRule = rules[0] || {};
            const isCommon =
              (!group.parameter_id &&
                !group.parameterId &&
                (group.parameter_name?.toLowerCase() === "common" ||
                  group.parameterName?.toLowerCase() === "common")) ||
              firstRule.mode === "common" ||
              firstRule.mode === "COMMON";

            const tableData = rules.map((rule: any, idx: number) =>
              mapRuleToRow(rule, groupIndex, idx, group),
            );

            if (isCommon) {
              return (
                <div
                  key={groupIndex}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <GenericTable
                    data={tableData}
                    columns={commonColumns}
                    rowKey="id"
                  />
                </div>
              );
            }

            const paramName = resolveParamName(firstRule, group);

            return (
              <div
                key={groupIndex}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Parameter:
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {paramName}
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-white text-gray-600 border-gray-200 rounded-full px-2 text-xs"
                    >
                      {rules.length} {rules.length === 1 ? "rule" : "rules"}
                    </Badge>
                  </div>
                </div>
                <GenericTable
                  data={tableData}
                  columns={paramWiseColumns}
                  rowKey="id"
                />
              </div>
            );
          })
        ) : (
          (() => {
            const firstRule = flatRules[0] || {};
            const isCommon =
              !firstRule.mode ||
              firstRule.mode === "common" ||
              firstRule.mode === "COMMON";

            const tableData = flatRules.map((rule: any, idx: number) =>
              mapRuleToRow(rule, 0, idx),
            );

            if (isCommon) {
              return (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <GenericTable
                    data={tableData}
                    columns={commonColumns}
                    rowKey="id"
                  />
                </div>
              );
            }

            return (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <GenericTable
                  data={tableData}
                  columns={paramWiseColumns}
                  rowKey="id"
                />
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default Deposits;
